import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PrintIcon from '@mui/icons-material/Print';
import SendIcon from '@mui/icons-material/Send';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
// Import and configure PDF.js worker using Vite's worker syntax
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker';

import { publicAsset } from '@utils/publicAsset';
import { useTracking } from '../../context/TrackingContext';
import { EventType } from '../../types/events';

import {
  A4_ASPECT_RATIO,
  createBlobUrl,
  isValidBase64,
  openPdfInNewTab,
  printPdfInHiddenIframe,
  revokeBlobUrl,
  safeBase64PdfToBlob,
  triggerDownload,
} from './utils';

// Import react-pdf styles
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
pdfjs.GlobalWorkerOptions.workerPort = new pdfWorker();

interface PdfEventMetadata {
  documentType?: string;
  patientId?: string;
  noteId?: string;
  documentId?: string;
}

interface PdfViewerBaseProps {
  filename: string;
  height?: string | number;
  onSendAction?: {
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    loading?: boolean;
    label: string;
  };
  eventMetadata?: PdfEventMetadata;
}

// Support either base64Data OR url (presigned URL)
type PdfViewerProps = PdfViewerBaseProps & (
  | { base64Data: string; url?: never }
  | { base64Data?: never; url: string }
);

export const PdfViewer: React.FC<PdfViewerProps> = ({
  base64Data,
  url,
  filename,
  height = '400px',
  onSendAction,
  eventMetadata,
}) => {
  const { trackEvent } = useTracking();
  const { pdf: pdfMetrics } = useEventTracking();
  const blobUrlRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const loadStartTimeRef = useRef<number>(0);
  const fileSizeRef = useRef<number>(0);

  // Determine if we're using a URL directly or need to create blob from base64
  const isUsingUrl = !!url;

  // State for URL-fetched blob
  const [fetchedBlobUrl, setFetchedBlobUrl] = useState<string | null>(null);
  const [corsBlocked, setCorsBlocked] = useState(false);
  const [isFetchingUrl, setIsFetchingUrl] = useState(false);

  // Fetch PDF from URL and convert to blob (to avoid CORS issues with react-pdf)
  useEffect(() => {
    if (!url) {
      setFetchedBlobUrl(null);
      setCorsBlocked(false);
      return;
    }

    let cancelled = false;
    const fetchPdf = async () => {
      setIsFetchingUrl(true);
      setCorsBlocked(false);
      loadStartTimeRef.current = performance.now();

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.status}`);
        }

        const blob = await response.blob();
        if (cancelled) return;

        // Validate blob size
        const maxSize = 50 * 1024 * 1024;
        fileSizeRef.current = Math.round(blob.size / 1024);
        if (blob.size > maxSize) {
          pdfMetrics.error({
            error_type: 'too_large',
            file_size_kb: fileSizeRef.current,
            document_type: eventMetadata?.documentType,
          });
          setIsFetchingUrl(false);
          return;
        }

        // Clean up previous blob URL
        if (blobUrlRef.current) {
          revokeBlobUrl(blobUrlRef.current);
        }

        const blobUrl = createBlobUrl(blob);
        blobUrlRef.current = blobUrl;
        setFetchedBlobUrl(blobUrl);
      } catch (err) {
        if (cancelled) return;
        // CORS error - fall back to external link mode
        console.warn('PDF fetch blocked (likely CORS), falling back to external link:', err);
        setCorsBlocked(true);
      } finally {
        if (!cancelled) {
          setIsFetchingUrl(false);
        }
      }
    };

    void fetchPdf();

    return () => {
      cancelled = true;
    };
  }, [url, eventMetadata?.documentType, pdfMetrics]);

  // Create blob URL from base64 data (for base64Data prop)
  const { base64BlobUrl, validationError } = useMemo(() => {
    // Skip if using URL mode
    if (url) {
      return { base64BlobUrl: '', validationError: null };
    }

    // Process base64 data
    if (!base64Data || !isValidBase64(base64Data)) {
      return { base64BlobUrl: '', validationError: 'Invalid PDF data' };
    }

    try {
      const blob = safeBase64PdfToBlob(base64Data);

      // Check if PDF validation failed
      if (!blob) {
        // Track PDF error
        pdfMetrics.error({
          error_type: 'corrupt',
          document_type: eventMetadata?.documentType,
        });
        return { base64BlobUrl: '', validationError: 'Invalid PDF content - file may be corrupted' };
      }

      // Check blob size (limit to 50MB)
      const maxSize = 50 * 1024 * 1024;
      fileSizeRef.current = Math.round(blob.size / 1024); // Store size in KB
      if (blob.size > maxSize) {
        // Track PDF error
        pdfMetrics.error({
          error_type: 'too_large',
          file_size_kb: fileSizeRef.current,
          document_type: eventMetadata?.documentType,
        });
        return { base64BlobUrl: '', validationError: 'PDF file too large (max 50MB)' };
      }

      const blobUrl = createBlobUrl(blob);

      // Clean up previous blob URL if exists
      if (blobUrlRef.current) {
        revokeBlobUrl(blobUrlRef.current);
      }

      blobUrlRef.current = blobUrl;
      loadStartTimeRef.current = performance.now(); // Start timing PDF load
      return { base64BlobUrl: blobUrl, validationError: null };
    } catch (err) {
      console.error('Error creating PDF blob:', err);
      // Track PDF error
      pdfMetrics.error({
        error_type: 'unsupported',
        document_type: eventMetadata?.documentType,
        error_message: 'Failed to process PDF data',
      });
      return { base64BlobUrl: '', validationError: 'Failed to process PDF data' };
    }
  }, [base64Data, url, eventMetadata?.documentType, pdfMetrics]);

  // Determine the final PDF source
  const pdfSource = isUsingUrl ? fetchedBlobUrl : base64BlobUrl;
  // Only show validation errors as actual errors, CORS blocking has its own UI
  const pdfError = !isUsingUrl ? validationError : null;

  // Set error if validation or fetch failed
  useEffect(() => {
    if (pdfError) {
      setError(pdfError);
      setLoading(false);
    }
  }, [pdfError]);

  // Cleanup blob URL on unmount (only for base64-created blobs, not external URLs)
  useEffect(() => {
    return () => {
      if (blobUrlRef.current && !isUsingUrl) {
        revokeBlobUrl(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [isUsingUrl]);

  // Track container dimensions for responsive PDF sizing
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current && height === '100%') {
        setContainerWidth(containerRef.current.offsetWidth);
        setContainerHeight(containerRef.current.offsetHeight);
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    // Use ResizeObserver for more accurate dimension tracking
    let resizeObserver: ResizeObserver | null = null;

    if (containerRef.current) {
      resizeObserver = new ResizeObserver(updateDimensions);
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateDimensions);
      // Always disconnect the observer if it exists, regardless of ref state
      if (resizeObserver) {
        try {
          resizeObserver.disconnect();
        } catch (error) {
          console.warn('Error disconnecting ResizeObserver:', error);
        }
      }
    };
  }, [height]);

  const handleOpenInNewTab = () => {
    if (pdfSource) {
      openPdfInNewTab(pdfSource);

      // Track PDF view interaction (keep legacy)
      if (eventMetadata) {
        trackEvent(EventType.PDF_INTERACTED, {
          interaction_type: 'view',
          user_type: 'provider',
          patient_id: eventMetadata.patientId,
          access_method: 'preview_modal',
          user_agent: navigator.userAgent,
          document_type: eventMetadata.documentType,
        });
      }
      
      // Track with Statsig
      pdfMetrics.interaction({
        interaction_type: 'view',
        document_type: eventMetadata?.documentType,
        pages_viewed: 1, // Viewing first page in thumbnail
      });
    }
  };

  const handleDownload = () => {
    if (pdfSource) {
      triggerDownload(pdfSource, filename);

      // Track PDF download interaction (keep legacy)
      if (eventMetadata) {
        trackEvent(EventType.PDF_INTERACTED, {
          interaction_type: 'download',
          user_type: 'provider',
          patient_id: eventMetadata.patientId,
          access_method: 'preview_modal',
          user_agent: navigator.userAgent,
          document_type: eventMetadata.documentType,
        });
      }
      
      // Track with Statsig
      pdfMetrics.interaction({
        interaction_type: 'download',
        document_type: eventMetadata?.documentType,
      });
      pdfMetrics.printExport({
        format: 'pdf',
        destination: 'file',
        page_count: numPages || undefined,
        document_type: eventMetadata?.documentType,
      });
    }
  };

  const handlePrint = () => {
    if (pdfSource) {
      printPdfInHiddenIframe(pdfSource);

      // Track PDF print interaction (keep legacy)
      if (eventMetadata) {
        trackEvent(EventType.PDF_INTERACTED, {
          interaction_type: 'print',
          user_type: 'provider',
          patient_id: eventMetadata.patientId,
          access_method: 'preview_modal',
          user_agent: navigator.userAgent,
          document_type: eventMetadata.documentType,
        });
      }
      
      // Track with Statsig
      pdfMetrics.interaction({
        interaction_type: 'print',
        document_type: eventMetadata?.documentType,
      });
      pdfMetrics.printExport({
        format: 'pdf',
        destination: 'printer',
        page_count: numPages || undefined,
        document_type: eventMetadata?.documentType,
      });
    }
  };


  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
    
    // Track PDF load performance
    if (loadStartTimeRef.current > 0) {
      const loadTime = Math.round(performance.now() - loadStartTimeRef.current);
      pdfMetrics.loadPerformance({
        load_time_ms: loadTime,
        file_size_kb: fileSizeRef.current || undefined,
        viewer_type: 'inline', // Since it's embedded in the component
        page_count: numPages,
        document_type: eventMetadata?.documentType,
      });
      loadStartTimeRef.current = 0; // Reset to prevent duplicate tracking
    }
  }, [pdfMetrics, eventMetadata]);

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('PDF load error:', error);
    setLoading(false);
    setError('Failed to load PDF');
    
    // Track PDF error
    pdfMetrics.error({
      error_type: 'load_failed',
      document_type: eventMetadata?.documentType,
      error_message: 'Failed to load PDF',
      file_size_kb: fileSizeRef.current || undefined,
    });
  }, [pdfMetrics, eventMetadata]);

  const onPageLoadSuccess = useCallback(() => {
    // Page loaded successfully
    setLoading(false);
  }, []);

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height,
          border: '1px solid',
          borderColor: 'error.main',
          borderRadius: 1,
          backgroundColor: 'error.lighter',
          p: 2,
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // CORS fallback UI - when we can't fetch the PDF due to S3 CORS restrictions
  // We still show action buttons since opening in new tab/download works
  if (corsBlocked && url) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          height: height === '100%' ? '100%' : { xs: 'auto', md: height },
          width: '100%',
        }}
      >
        {/* Placeholder Preview */}
        <Box
          sx={{
            position: 'relative',
            cursor: 'pointer',
            borderRadius: 1,
            overflow: 'hidden',
            boxShadow: 2,
            backgroundColor: 'grey.100',
            flex: { xs: '0 0 auto', md: '1 1 auto' },
            width: { xs: '100%', md: 'auto' },
            height: { xs: 'auto', md: '100%' },
            minWidth: { md: 300 },
            minHeight: 300,
            aspectRatio: { xs: `1 / ${A4_ASPECT_RATIO}`, md: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            '&:hover': {
              boxShadow: 4,
              backgroundColor: 'grey.200',
            },
          }}
          onClick={handleOpenInNewTab}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter') handleOpenInNewTab();
          }}
          aria-label="Click to open PDF in new tab"
        >
          <Box
            component="img"
            src={publicAsset('/logo-128.png')}
            alt="PDF Document"
            sx={{
              width: 64,
              height: 64,
              opacity: 0.5,
              mb: 2,
            }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            PDF Document
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            Click to view in new tab
          </Typography>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={e => {
              e.stopPropagation();
              handleOpenInNewTab();
            }}
          >
            Open PDF
          </Button>
        </Box>

        {/* Action Buttons */}
        <Stack
          spacing={2}
          sx={{
            justifyContent: 'flex-start',
            flex: '0 0 auto',
            width: { xs: '100%', md: 'auto' },
            minWidth: { md: 200 },
          }}
        >
          {onSendAction && (
            <Button
              variant="contained"
              size="medium"
              startIcon={<SendIcon />}
              onClick={e => {
                pdfMetrics.interaction({
                  interaction_type: 'send',
                  document_type: eventMetadata?.documentType,
                });
                onSendAction.onClick(e);
              }}
              disabled={onSendAction.loading}
              fullWidth
            >
              {onSendAction.label}
            </Button>
          )}

          <Button
            variant="outlined"
            size="medium"
            startIcon={<PrintIcon />}
            onClick={() => {
              // For CORS-blocked URLs, open in new tab and let user print from there
              window.open(url, '_blank');
              pdfMetrics.interaction({
                interaction_type: 'print',
                document_type: eventMetadata?.documentType,
              });
            }}
            fullWidth
          >
            Print
          </Button>

          <Button
            variant="outlined"
            size="medium"
            startIcon={<DownloadIcon />}
            onClick={() => {
              triggerDownload(url, filename);
              pdfMetrics.interaction({
                interaction_type: 'download',
                document_type: eventMetadata?.documentType,
              });
              pdfMetrics.printExport({
                format: 'pdf',
                destination: 'file',
                document_type: eventMetadata?.documentType,
              });
            }}
            fullWidth
          >
            Download
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 3,
        height: height === '100%' ? '100%' : { xs: 'auto', md: height },
        width: '100%',
      }}
    >
      {/* PDF Thumbnail Preview */}
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          cursor: 'pointer',
          borderRadius: 1,
          overflow: 'hidden',
          boxShadow: 2,
          backgroundColor: '#fff',
          flex: { xs: '0 0 auto', md: '1 1 auto' }, // Make it grow on desktop
          width: { xs: '100%', md: 'auto' },
          height: { xs: 'auto', md: '100%' },
          minWidth: { md: 300 }, // Minimum width to ensure readability
          maxWidth: { xs: '100%', md: '100%' },
          // For mobile, use aspect-ratio to maintain A4 proportions
          aspectRatio: { xs: `1 / ${A4_ASPECT_RATIO}`, md: 'auto' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': {
            boxShadow: 4,
            '& .pdf-overlay': {
              opacity: 1,
            },
          },
        }}
        onClick={handleOpenInNewTab}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter') handleOpenInNewTab();
        }}
        aria-label="Click to open PDF in new tab"
      >
        {(loading || isFetchingUrl) && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 1,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}

        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            overflow: 'auto',
          }}
        >
          <Document
            file={pdfSource}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={null}
            error={null}
          >
            <Page
              pageNumber={1}
              width={height === '100%' && containerWidth > 0 ? containerWidth : undefined}
              height={height === '100%' && containerHeight > 0 ? containerHeight : undefined}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              onLoadSuccess={onPageLoadSuccess}
              loading={null}
            />
          </Document>
        </Box>

        {/* Click to open overlay - shown on hover */}
        <Box
          className="pdf-overlay"
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
            color: 'white',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            opacity: 0,
            transition: 'opacity 0.3s ease-in-out',
          }}
        >
          <OpenInNewIcon fontSize="small" />
          <Typography variant="caption">Click to view full PDF</Typography>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Stack
        spacing={2}
        sx={{
          justifyContent: 'flex-start',
          flex: '0 0 auto', // Don't grow or shrink
          width: { xs: '100%', md: 'auto' }, // Auto width based on content
          minWidth: { md: 200 }, // Minimum width to ensure buttons don't get too small
        }}
      >
        {onSendAction && (
          <Button
            variant="contained"
            size="medium"
            startIcon={<SendIcon />}
            onClick={(e) => {
              // Track send action
              pdfMetrics.interaction({
                interaction_type: 'send',
                document_type: eventMetadata?.documentType,
              });
              onSendAction.onClick(e);
            }}
            disabled={onSendAction.loading || !pdfSource || loading}
            fullWidth
          >
            {onSendAction.label}
          </Button>
        )}

        {/* <Button
          variant="outlined"
          size="medium"
          startIcon={<Save />}
          onClick={handleSaveToPortal}
          fullWidth
        >
          Save to {subjectLanguageVariant.singularCap} Portal
        </Button> */}

        <Button
          variant="outlined"
          size="medium"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={!pdfSource || loading}
          fullWidth
        >
          Print
        </Button>

        <Button
          variant="outlined"
          size="medium"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={!pdfSource || loading}
          fullWidth
        >
          Download
        </Button>

        {numPages && numPages > 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Page 1 of {numPages}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
